import { getConfig, resetConfigCache } from './configApi';

/**
 * `$cookies.put(name, value)` with no options, which is what `tenantService` called.
 *
 * Angular wrote a session cookie at path `/` with no explicit expiry, domain or SameSite, and
 * the server reads both names on every request. Writing it by hand rather than adding a cookie
 * library for two calls; the encoding is the same because `$cookies` also used
 * `encodeURIComponent` on the value.
 */
const putCookie = (name, value) => {
  // `unicorn/no-document-cookie` wants a cookie library. Adding one for two writes is not
  // worth a dependency, particularly in the change that removed 26 of them, and the encoding
  // and attributes here are exactly what `$cookies.put` produced.
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${name}=${encodeURIComponent(value)};path=/`;
};

/**
 * Tenant and subtenant state, and the switch between them.
 *
 * A port of `modules/sidebar/tenantService.js`, which was itself the leftover logic of the
 * Angular `tenantSelector` directive after its template became the React rail in SURF-4808.
 * It published five values on `$rootScope` for React to read back through props; now React
 * holds them directly and the round trip is gone.
 *
 * **Switching writes a cookie and reloads the page, and that is deliberate.** The tenant
 * decides what `/config/config.json` returns for every subsequent request, so the whole app
 * has to be rebuilt from the new config. A client-side route change would leave the CMS
 * holding the previous tenant's menu, messages and plugin list.
 */
export async function loadTenantState() {
  const config = await getConfig();
  const messages = config.messages || {};
  const tenants = config.accessibleTenants;

  const base = {
    tenantSelectorLabel: messages['cms.tenantSelectorLabel'] || 'Chapter',
    subtenantSelectorLabel: messages['cms.subtenantSelectorLabel'] || 'Subtenant',
    tenants,
    showTenants: !!tenants && tenants.length > 1,
    showSubtenants: false,
    selectedTenant: null,
    selectedSubtenant: null,
  };
  if (!tenants) return base;

  const selectedTenant = tenants.find((tenant) => tenant.tenantId === config.tenantId);
  if (!selectedTenant) return base;

  const selectedSubtenant = selectedTenant.subtenants[config.subtenantId];
  // Written for the server, which reads both on every request. The Angular service set them
  // here too, as a side effect of rendering the picker.
  putCookie('tenantId', selectedTenant.tenantId);
  if (selectedSubtenant) putCookie('subtenantId', selectedSubtenant.id);

  return {
    ...base,
    selectedTenant,
    selectedSubtenant,
    showSubtenants: Object.values(selectedTenant.subtenants).length > 1,
  };
}

function switchTo(cookieName, value) {
  putCookie(cookieName, value);
  window.location.replace('/');
}

export const setTenant = (tenant) => switchTo('tenantId', tenant.tenantId);
export const setSubtenant = (subtenant) => switchTo('subtenantId', subtenant.id);

/**
 * Reload after a subtenant is created or renamed elsewhere in the CMS.
 *
 * `$rootScope.$on('subtenantUpdated')` did this. The config is dropped first because the
 * subtenant list lives in it.
 */
export async function reloadTenantState() {
  resetConfigCache();
  return loadTenantState();
}
